import type { PublicRelease } from "../api/types";
import { confirmedRarities } from "../catalog/rarity";
import type { GridDensity } from "../ui/grid-density";
import { RarityBadge } from "./RarityBadge";

function collectorLabel(release: PublicRelease): string | null {
  if (release.collectorNumber === null) {
    return null;
  }
  if (release.collectorTotal === null) {
    return String(release.collectorNumber);
  }
  return `${release.collectorNumber}/${release.collectorTotal}`;
}

export function formatReleaseCollector(release: PublicRelease): string {
  return collectorLabel(release) ?? "";
}

export function formatPackContext(release: PublicRelease): string | null {
  if (!release.packName && !release.packCode) {
    return null;
  }
  const name = release.packName ? release.packName.replace(/\s*5-Packs?$/i, "").trim() : "";
  if (name) {
    return name;
  }
  return release.packName;
}

export function formatCompactVariation(release: PublicRelease): string {
  if (release.packName && release.color && release.variation) {
    const quoted = release.variation.match(/^"([^"]+)"/);
    const nickname = quoted?.[1]?.trim() ?? "";
    return `${release.color} · ${nickname || release.variation}`;
  }
  return release.variation ?? "";
}

export function formatSeriesPosition(release: PublicRelease): string | null {
  if (release.seriesNumber === null) {
    return null;
  }
  if (release.seriesTotal === null) {
    return String(release.seriesNumber);
  }
  return `${release.seriesNumber}/${release.seriesTotal}`;
}

type ReleaseMetaProps = {
  release: PublicRelease;
  headingLevel?: "h1" | "h2" | "h3";
  compact?: boolean;
  density?: GridDensity;
};

export function ReleaseMeta({
  release,
  headingLevel = "h2",
  compact = false,
  density = 2,
}: ReleaseMetaProps) {
  const Heading = headingLevel;
  const rarities = confirmedRarities(release);
  const seriesPosition = formatSeriesPosition(release);

  if (compact) {
    const showSeries = density !== 3;
    const compactVariation = formatCompactVariation(release);
    return (
      <div className={`hw-release-meta hw-release-meta--compact hw-release-meta--d${density}`}>
        <Heading className="hw-release-meta__name">{release.casting.name}</Heading>
        {showSeries ? (
          <p className="hw-release-meta__line">
            <span className="hw-release-meta__year">{release.year}</span>
            {release.packName ? (
              <>
                <span className="hw-release-meta__pack">5-Pack</span>
                <span className="hw-release-meta__series">{formatPackContext(release)}</span>
              </>
            ) : release.seriesName ? (
              <span className="hw-release-meta__series">{release.seriesName}</span>
            ) : null}
          </p>
        ) : (
          <p className="hw-release-meta__line hw-release-meta__line--year">
            <span className="hw-release-meta__year">{release.year}</span>
            {release.packName ? (
              <>
                <span className="hw-release-meta__pack">5-Pack</span>
                <span className="hw-release-meta__series">{formatPackContext(release)}</span>
              </>
            ) : null}
          </p>
        )}
        <p
          className={
            compactVariation
              ? "hw-release-meta__variation"
              : "hw-release-meta__variation hw-release-meta__variation--empty"
          }
        >
          {compactVariation}
        </p>
        {density === 1 && release.color ? (
          <p className="hw-release-meta__sub">{release.color}</p>
        ) : null}
      </div>
    );
  }

  const details: Array<{ label: string; value: string }> = [];
  details.push({ label: "Год", value: String(release.year) });
  const collector = collectorLabel(release);
  if (collector) {
    details.push({ label: "Номер", value: collector });
  }
  if (release.packName) {
    details.push({ label: "Набор", value: release.packName });
  }
  if (release.packCode) {
    details.push({ label: "Код набора", value: release.packCode });
  }
  if (release.seriesName) {
    details.push({ label: "Серия", value: release.seriesName });
  }
  if (seriesPosition) {
    details.push({ label: "В серии", value: seriesPosition });
  }
  if (release.color) {
    details.push({ label: "Цвет", value: release.color });
  }
  if (release.variation) {
    details.push({ label: "Вариация", value: release.variation });
  }

  return (
    <div className="hw-release-meta">
      <Heading className="hw-release-meta__name">{release.casting.name}</Heading>
      <dl className="hw-release-meta__facts">
        {details.map((detail) => (
          <div key={detail.label} className="hw-release-meta__fact">
            <dt>{detail.label}</dt>
            <dd>{detail.value}</dd>
          </div>
        ))}
      </dl>
      {rarities.length > 0 ? (
        <ul className="hw-release-meta__flags">
          {rarities.map((rarity) => (
            <li key={rarity}>
              <RarityBadge rarity={rarity} />
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
